import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Search,
  MessageSquare,
  Star,
  Megaphone,
  PlusCircle,
  BarChart3,
  Wallet,
  ChevronDown,
  ChevronRight,
  Loader2,
  Tag,
} from 'lucide-react';
import { useGetCategoryTreeQuery, useGetLegalPageQuery } from '@nimbazaar/store';
import { LucideIcon } from './LucideIcon';

const buyerFeatures = [
  { name: 'Browse Ads', icon: Search, route: '/ads' },
  { name: 'My Deals', icon: MessageSquare, route: '/inbox' },
  { name: 'My Bids', icon: Star, route: '/account/my-bids' },
  { name: 'Wallet', icon: Wallet, route: '/account/wallet' },
];

const sellerFeatures = [
  { name: 'Post Ad', icon: PlusCircle, route: '/post-ad' },
  { name: 'My Deals', icon: MessageSquare, route: '/inbox' },
  { name: 'My Ads', icon: Megaphone, route: '/account/my-ads' },
  { name: 'Wallet', icon: Wallet, route: '/account/wallet' },
];

const DEFAULT_SLIDES = [
  { id: 1, badge: 'Live Now - Limited Time', heading: '#WANTED ADS', subtitle: 'Post what you need, let sellers come to you. The smarter way to shop.', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&h=400&fit=crop' },
  { id: 2, badge: 'New Feature', heading: '#SMART BIDDING', subtitle: 'Get the best deals from verified sellers competing for your business.', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1920&h=400&fit=crop' },
  { id: 3, badge: 'Join Community', heading: '#TRUSTED SELLERS', subtitle: 'Connect with thousands of verified sellers ready to fulfill your needs.', image: 'https://images.unsplash.com/photo-1556742111-a301076d9d18?w=1920&h=400&fit=crop' },
];

const CATEGORY_COLORS = [
  { text: 'text-blue-500', bg: 'bg-blue-50' },
  { text: 'text-emerald-500', bg: 'bg-emerald-50' },
  { text: 'text-pink-500', bg: 'bg-pink-50' },
  { text: 'text-green-500', bg: 'bg-green-50' },
  { text: 'text-orange-500', bg: 'bg-orange-50' },
  { text: 'text-purple-500', bg: 'bg-purple-50' },
  { text: 'text-amber-600', bg: 'bg-amber-50' },
  { text: 'text-red-500', bg: 'bg-red-50' },
  { text: 'text-cyan-500', bg: 'bg-cyan-50' },
  { text: 'text-slate-600', bg: 'bg-slate-100' },
];

export function Hero() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const { data: treeData, isLoading: isCategoriesLoading } = useGetCategoryTreeQuery();
  const { data: heroData } = useGetLegalPageQuery('hero-banner');
  const categories = treeData?.data ?? [];

  const DEFAULT_OVERLAY = { enabled: true, color: '#1e3a8a', opacity: 75 };

  const heroConfig = (() => {
    const content = heroData?.data?.content;
    if (content) {
      try {
        // Clean newlines/tabs inside JSON string values before parsing
        const cleaned = content.replace(/[\r\n\t]+\s*/g, ' ');
        const parsed = JSON.parse(cleaned);
        if (Array.isArray(parsed)) {
          return { overlay: DEFAULT_OVERLAY, slides: parsed };
        }
        return { overlay: parsed.overlay || DEFAULT_OVERLAY, slides: parsed.slides || DEFAULT_SLIDES };
      } catch { return { overlay: DEFAULT_OVERLAY, slides: DEFAULT_SLIDES }; }
    }
    return { overlay: DEFAULT_OVERLAY, slides: DEFAULT_SLIDES };
  })();

  const bannerSlides = heroConfig.slides;
  const overlay = {
    enabled: heroConfig.overlay.enabled !== false,
    color: heroConfig.overlay.color || '#1e3a8a',
    opacity: heroConfig.overlay.opacity !== undefined ? Number(heroConfig.overlay.opacity) : 75,
  };

  const searchByCategory = (categoryId: string) => {
    const params = new URLSearchParams();
    params.set('categoryId', categoryId);
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    navigate(`/ads?${params.toString()}`);
    setSearchQuery('');
    setIsCategoryOpen(false);
  };

  // Auto-slide every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slide = bannerSlides[currentSlide];

  return (
    <section>
      {/* Hero Banner with Background Image */}
      <div className="relative h-[180px] sm:h-[280px] md:h-[320px] overflow-hidden">
        {/* Background Image Slider */}
        <div className="absolute inset-0 w-full h-full">
          {bannerSlides.map((s, index) => (
            <img
              key={s.id}
              src={s.image}
              alt={s.heading}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
                currentSlide === index ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ objectPosition: 'center center' }}
            />
          ))}
          {/* Overlay */}
          {overlay.enabled && (
            <div
              className="absolute inset-0"
              style={{
                background: overlay.color,
                opacity: overlay.opacity / 100,
              }}
            />
          )}
        </div>

        {/* Content */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-3 sm:px-4 flex items-center">
          <div className="max-w-2xl space-y-2 sm:space-y-5">
            {/* Date Badge */}
            <div
              className="inline-block bg-secondary-500 text-white text-[11px] sm:text-sm font-medium px-3 sm:px-4 py-0.5 sm:py-1 rounded-full transition-opacity duration-500"
              key={slide.badge}
            >
              {slide.badge}
            </div>

            {/* Main Heading */}
            <h1
              className="text-xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white transition-opacity duration-500"
              key={slide.heading}
            >
              <span className="text-secondary-400">#</span>{slide.heading.slice(1)}
            </h1>

            {/* Subtitle */}
            <p
              className="text-gray-200 text-xs sm:text-base md:text-xl max-w-lg transition-opacity duration-500 line-clamp-2 sm:line-clamp-none"
              key={slide.subtitle}
            >
              {slide.subtitle}
            </p>

          </div>
        </div>
      </div>

      {/* Mobile: Search Bar in place of cards */}
      <div className="relative -mt-6 z-20 pb-4 sm:hidden">
        <div className="max-w-7xl mx-auto px-3">
          <div className="relative bg-white rounded-xl shadow-lg p-3 space-y-3">
            {/* Search Bar */}
            <div className="flex items-center">
              {/* Category Dropdown Button */}
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className="flex items-center gap-1 bg-primary-500 hover:bg-primary-600 text-white px-2.5 h-10 rounded-l-lg transition-colors shrink-0"
              >
                {isCategoriesLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : null}
                <span className="text-xs font-medium">All</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Search Input */}
              <input
                type="text"
                placeholder="Search for wanted ads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const params = new URLSearchParams();
                    if (searchQuery.trim()) params.set('search', searchQuery.trim());
                    const qs = params.toString();
                    navigate(`/ads${qs ? `?${qs}` : ''}`);
                    setSearchQuery('');
                  }
                }}
                className="w-full px-3 h-10 text-sm text-gray-900 placeholder-gray-400 focus:outline-none bg-gray-50 border-y border-gray-200"
              />

              {/* Search Button */}
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  if (searchQuery.trim()) params.set('search', searchQuery.trim());
                  const qs = params.toString();
                  navigate(`/ads${qs ? `?${qs}` : ''}`);
                  setSearchQuery('');
                }}
                className="bg-primary-500 hover:bg-primary-600 px-3.5 h-10 rounded-r-lg transition-colors"
              >
                <Search className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Category Dropdown Menu - full width of card */}
            {isCategoryOpen && (
              <div className="absolute left-0 right-0 top-[52px] mx-0 max-h-[60vh] bg-white rounded-b-xl shadow-2xl border border-gray-100 z-50 overflow-y-auto">
                <h3 className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider sticky top-0 bg-white">Categories</h3>
                {categories.map((category, index) => {
                  const color = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
                  return (
                    <div key={category.id}>
                      <button
                        onClick={() => searchByCategory(category.id)}
                        className="w-full text-left px-4 py-3 text-sm transition-all flex items-center gap-3 hover:bg-gray-50"
                      >
                        <div className={`w-8 h-8 rounded-lg ${color.bg} flex items-center justify-center`}>
                          <LucideIcon
                            name={category.icon || ''}
                            className={`w-4 h-4 ${color.text}`}
                            fallback={<Tag className={`w-4 h-4 ${color.text}`} />}
                          />
                        </div>
                        <span className="font-medium flex-1 text-gray-900">{category.name}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                      </button>
                      {category.children.length > 0 && (
                        <div className="pl-14 pr-4 pb-2 space-y-0.5">
                          {category.children.map((sub) => (
                            <button
                              key={sub.id}
                              onClick={() => searchByCategory(sub.id)}
                              className="w-full text-left px-2 py-2 text-sm text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                            >
                              {sub.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quick Action Buttons */}
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { name: 'Browse Ads', icon: Search, route: '/ads', color: 'text-primary-500', bg: 'bg-primary-50' },
                { name: 'Post Ad', icon: PlusCircle, route: '/post-ad', color: 'text-secondary-500', bg: 'bg-secondary-50' },
                { name: 'My Ads', icon: Megaphone, route: '/account/my-ads', color: 'text-primary-500', bg: 'bg-primary-50' },
                { name: 'My Bids', icon: Star, route: '/account/my-bids', color: 'text-secondary-500', bg: 'bg-secondary-50' },
              ].map((item) => {
                const IconComponent = item.icon;
                return (
                  <button
                    key={item.name}
                    onClick={() => navigate(item.route)}
                    className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className={`w-8 h-8 ${item.bg} rounded-lg flex items-center justify-center`}>
                      <IconComponent className={`w-4 h-4 ${item.color}`} />
                    </div>
                    <span className="text-[10px] font-medium text-gray-600 leading-tight text-center">{item.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Click outside to close category dropdown */}
        {isCategoryOpen && (
          <div
            className="fixed inset-0 z-40 bg-transparent"
            onClick={() => setIsCategoryOpen(false)}
          />
        )}
      </div>

      {/* Desktop: Buyer/Seller Cards - hidden on mobile */}
      <div className="relative -mt-12 z-20 pb-8 hidden sm:block">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-6">
          {/* Buyer Card */}
          <div className="bg-primary-50 rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-primary-500" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">I'm a Buyer</h3>
                <p className="text-sm text-gray-500">Find exactly what you need</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {buyerFeatures.map((feature) => {
                const IconComponent = feature.icon;
                return (
                  <button
                    key={feature.name}
                    onClick={() => navigate(feature.route)}
                    className="flex-1 flex flex-col items-center gap-2 p-3 bg-white hover:bg-primary-100 rounded-xl transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-primary-100 group-hover:bg-primary-200 rounded-lg flex items-center justify-center transition-colors shadow-sm">
                      <IconComponent className="w-5 h-5 text-primary-500" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 group-hover:text-primary-600">
                      {feature.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Seller Card */}
          <div className="bg-secondary-50 rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center">
                <Megaphone className="w-6 h-6 text-secondary-500" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">I'm a Seller</h3>
                <p className="text-sm text-gray-500">Respond to buyer requests</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {sellerFeatures.map((feature) => {
                const IconComponent = feature.icon;
                return (
                  <button
                    key={feature.name}
                    onClick={() => navigate(feature.route)}
                    className="flex-1 flex flex-col items-center gap-2 p-3 bg-white hover:bg-secondary-100 rounded-xl transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-secondary-100 group-hover:bg-secondary-200 rounded-lg flex items-center justify-center transition-colors shadow-sm">
                      <IconComponent className="w-5 h-5 text-secondary-500" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 group-hover:text-secondary-600">
                      {feature.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
